import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchBTNtoUSDRate, convertBTNtoUSD } from "../utils/currencyUtils.js";
import {
  findServiceIndex,
  groupSubServicesByCategory,
  extractNumericBTN,
} from "../utils/serviceModalUtils.js";

import Header from "../components/Header.js";
import Footer from "../components/Footer.js";

import servicesData from "../data/servicesData.js";

import spaServiceImage from "../assets/images/spa-service.jpg";
import salonServiceImage from "../assets/images/salon-service.png";

function ServicesPage() {
  const location = useLocation();
  const [activeService, setActiveService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const categoryRefs = useRef({});

  const [usdPrices, setUsdPrices] = useState({});

  useEffect(() => {
    // Parse query parameters
    const params = new URLSearchParams(location.search);
    const service = params.get("service");
    const category = params.get("category");

    if (service) {
      const serviceIndex = findServiceIndex(service);
      if (serviceIndex !== -1) {
        setActiveService(serviceIndex);
        setShowModal(true);

        setTimeout(() => {
          const categoryRef = categoryRefs.current[category];
          if (categoryRef) {
            categoryRef.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 300);
      }
    }
  }, [location.search]);

  const openModal = (index) => {
    setActiveService(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setActiveService(null);
    setShowModal(false);
  };

  const handleBookNow = (serviceTitle, subServiceTitle, subServicePrice) => {
    navigate(
      `/booking?service=${encodeURIComponent(
        serviceTitle
      )}&subService=${encodeURIComponent(
        subServiceTitle
      )}&price=${encodeURIComponent(subServicePrice)}`
    );
  };

  const handleConvertToUSD = async (usdKey, btnAmount) => {
    const numericBTN = extractNumericBTN(btnAmount);

    if (!numericBTN || isNaN(numericBTN)) {
      setUsdPrices((prev) => ({
        ...prev,
        [usdKey]: { usd: null, loading: false, error: "Invalid price" },
      }));
      return;
    }

    setUsdPrices((prev) => ({
      ...prev,
      [usdKey]: { usd: null, loading: true, error: null },
    }));

    try {
      const rate = await fetchBTNtoUSDRate();
      const usd = convertBTNtoUSD(numericBTN, rate);
      setUsdPrices((prev) => ({
        ...prev,
        [usdKey]: { usd, loading: false, error: null },
      }));
    } catch (error) {
      setUsdPrices((prev) => ({
        ...prev,
        [usdKey]: {
          usd: null,
          loading: false,
          error: "Conversion failed",
        },
      }));
    }
  };

  return (
    <>
      <Header />
      <div className="services-page bg-gradient-to-br from-gray-50 via-amber-50/30 to-white">
        {/* Enhanced Hero Section */}
        <section className="hero relative bg-gradient-to-br from-amber-800 via-amber-900 to-red-900 text-white py-12 overflow-hidden">
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-100 to-yellow-200 drop-shadow-2xl animate-fade-in">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed text-amber-100 font-light animate-fade-in delay-300">
              Discover a wide range of services tailored to meet your needs. We
              offer SPA and Salon service.
            </p>
          </div>
        </section>

        {/* Enhanced Services Section */}
        <section className="services relative py-24 overflow-hidden">
          {/* Background decorative elements */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl from-amber-200/20 to-yellow-300/10 rounded-full blur-2xl translate-x-1/3 -translate-y-1/3"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-orange-200/20 to-red-300/10 rounded-full blur-2xl -translate-x-1/3 translate-y-1/3"></div>

          <div className="container mx-auto px-6 text-center relative z-10">
            <div className="mb-16">
              <h2 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 via-amber-700 to-gray-800 mb-4 drop-shadow-2xl">
                Explore Our Offerings
              </h2>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent rounded-full mx-auto animate-pulse"></div>
            </div>

            <div className="flex flex-wrap gap-10 justify-center items-center">
              {servicesData.map((service, index) => (
                <div
                  key={index}
                  className="group service-card bg-gradient-to-br from-white via-amber-50/30 to-white shadow-2xl rounded-3xl overflow-hidden transition-all duration-500 transform hover:scale-105 hover:shadow-amber-200/50 w-80 cursor-pointer border border-amber-100"
                  onClick={() => openModal(index)}
                >
                  <div className="relative service-image h-56 bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
                    <img
                      src={
                        service.title === "Ku-Nye Services"
                          ? spaServiceImage
                          : salonServiceImage
                      }
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />

                    {/* Overlay effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Floating particles */}
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-amber-400 rounded-full animate-ping opacity-75"></div>
                    <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-yellow-400 rounded-full animate-ping opacity-75 delay-500"></div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-amber-700 mb-2 group-hover:from-amber-700 group-hover:to-amber-800 transition-all duration-300">
                      {service.title}
                    </h3>

                    {/* Hover indicator */}
                    <div className="mt-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="text-amber-600 font-semibold text-sm">
                        Click to explore
                      </span>
                      <i className="fas fa-arrow-right ml-2 text-amber-600 transform group-hover:translate-x-1 transition-transform duration-300"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Modal for Sub-services */}
        {showModal && activeService !== null && (
          <div className="modal fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="modal-content bg-gradient-to-br from-white via-gray-50/50 to-white rounded-3xl shadow-2xl p-8 w-full md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-hidden border border-gray-200 transform animate-modal-in">
              <div className="flex justify-between items-center mb-8">
                <h3 className="modal-title text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800 drop-shadow-lg">
                  {servicesData[activeService].title}
                </h3>
                <button
                  className="close-button group w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 transition-all duration-300 transform hover:scale-110"
                  onClick={closeModal}
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="sub-services space-y-8 overflow-y-auto max-h-[70vh] pr-4 custom-scrollbar">
                {Object.entries(
                  groupSubServicesByCategory(
                    servicesData[activeService].subServices
                  )
                ).map(([category, subServices]) => (
                  <div
                    key={category}
                    ref={(el) => (categoryRefs.current[category] = el)}
                    className="category-section mb-8"
                  >
                    {/* Enhanced category heading */}
                    <div className="category-heading bg-gradient-to-r from-amber-100 via-amber-200 to-amber-100 text-amber-900 px-6 py-4 rounded-2xl shadow-lg mb-6 border border-amber-200">
                      <h4 className="text-3xl font-bold flex items-center">
                        <i className="fas fa-spa mr-3 text-amber-700"></i>
                        {category}
                      </h4>
                    </div>

                    {/* Enhanced sub-services under the category */}
                    <div className="space-y-6">
                      {subServices.map((subService, subIndex) => {
                        const usdKey = `${category}-${subIndex}`;
                        return (
                          <div
                            key={usdKey}
                            className="sub-service-item bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-100 "
                          >
                            <h5 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-blue-700 mb-3">
                              {subService.title}
                            </h5>

                            {subService.benefits && (
                              <div className="mb-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
                                <p className="text-gray-700 font-medium">
                                  <span className="font-bold text-green-700">
                                    Benefits:
                                  </span>{" "}
                                  {subService.benefits}
                                </p>
                              </div>
                            )}

                            {subService.duration && (
                              <div className="mb-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                                <p className="text-gray-700 font-medium">
                                  <span className="font-bold text-blue-700">
                                    Duration:
                                  </span>{" "}
                                  {subService.duration}
                                </p>
                              </div>
                            )}

                            <div className="mb-4 p-3 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                              <p className="text-gray-800 font-bold text-lg">
                                <span className="text-amber-700">Price:</span>{" "}
                                {subService.price}
                              </p>
                            </div>

                            {/* Enhanced USD conversion section */}
                            <div className="px-4 mt-2">
                              {usdPrices[usdKey]?.loading && (
                                <div className="flex items-center text-blue-600 text-sm mt-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                  Converting...
                                </div>
                              )}
                              {usdPrices[usdKey]?.usd && (
                                <p className="text-green-600 font-semibold text-base mt-2 flex items-center">
                                  <i className="fas fa-dollar-sign mr-2"></i>≈ $
                                  {usdPrices[usdKey].usd} USD
                                  {typeof subService.price === "string" &&
                                    subService.price.includes("-") && (
                                      <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                        (minimum)
                                      </span>
                                    )}
                                </p>
                              )}
                              {usdPrices[usdKey]?.error && (
                                <p className="text-red-600 font-medium text-sm mt-2 flex items-center">
                                  <i className="fas fa-exclamation-triangle mr-2"></i>
                                  {usdPrices[usdKey].error}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-4 mt-6">
                              <button
                                className="group relative bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-full shadow-lg font-bold text-sm hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 overflow-hidden"
                                onClick={() =>
                                  handleBookNow(
                                    servicesData[activeService].title,
                                    subService.title,
                                    subService.price
                                  )
                                }
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                <span className="relative flex items-center">
                                  Book Now
                                  <i className="fas fa-calendar-plus ml-2"></i>
                                </span>
                              </button>

                              <span className="h-8 border-l-2 border-gray-300"></span>

                              <button
                                className="group px-5 py-3 rounded-full border-2 border-blue-600 text-blue-600 bg-white font-bold text-sm shadow-lg hover:bg-blue-50 hover:border-blue-700 transition-all duration-300 transform hover:scale-105"
                                onClick={() =>
                                  handleConvertToUSD(usdKey, subService.price)
                                }
                              >
                                <span className="flex items-center">
                                  Convert to USD
                                  <i className="fas fa-exchange-alt ml-2 transform group-hover:rotate-180 transition-transform duration-300"></i>
                                </span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />

      {/* Custom styles */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes modal-in {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-modal-in {
          animation: modal-in 0.3s ease-out forwards;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #1d4ed8);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #1d4ed8, #1e40af);
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-pulse,
          .animate-ping,
          .animate-fade-in,
          .animate-modal-in {
            animation: none;
          }
        }
      `}</style>
    </>
  );
}

export default ServicesPage;
